/**
 * Live signals that the phone's own detector has stopped being usable
 * mid-scan — the camera-on truth the startup benchmark cannot see (spec §4.6).
 *
 * Pure classes with the time passed in, plus module singletons that
 * `PoseDetectionManager` writes and `serverPoseService` and the alignment
 * accumulator read. Same shape as `modelInitState.ts`, for the same reason:
 * a module that imports nothing cannot close an import cycle.
 *
 * The first three only ever widen (phone → server). `JointUnseenWhileFramed`
 * judges whichever model is running, so it can point either way — the
 * controller decides what a cloud miss may do. Once tripped, every signal stays
 * tripped for the attempt; `PoseDetectionManager.resetAttemptMetrics` clears
 * them at the next ALIGNMENT entry.
 */

/** Consecutive detect errors that mean "broken" when no timeout occurred. */
export const BROKEN_AFTER_ERRORS = 3;

export class DetectorHealth {
  private consecutiveErrors = 0;
  private totalErrors = 0;
  private broken = false;

  recordSuccess(): void {
    this.consecutiveErrors = 0;
  }

  recordError(): void {
    this.totalErrors++;
    this.consecutiveErrors++;
    if (this.consecutiveErrors >= BROKEN_AFTER_ERRORS) this.broken = true;
  }

  /**
   * One is enough — for a SCAN detect. `DETECT_TIMEOUT_MS` is 5 s against a
   * warm detector's ~340 ms worst cycle, so a single timeout is a lost message,
   * and the alignment loop awaits every detect. The warm-up and the startup
   * check never reach here: a cold first detection takes a median 14 s on
   * iPhone first visits (`BOOT_DETECT_TIMEOUT_MS`).
   */
  recordTimeout(): void {
    this.totalErrors++;
    this.broken = true;
  }

  recordWorkerCrash(): void {
    this.broken = true;
  }

  isBroken(): boolean {
    return this.broken;
  }

  errors(): number {
    return this.totalErrors;
  }

  reset(): void {
    this.consecutiveErrors = 0;
    this.totalErrors = 0;
    this.broken = false;
  }
}

export const BLIND_SCORE_FLOOR = 0.15;
export const BLIND_AFTER_MS = 3000;
/** `POSE_LOST_GAP_MS`: a longer gap is "could not see them", not a run. */
export const BLIND_GAP_TOLERANCE_MS = 500;
/** The app's framed band, `alignmentAccumulator.ts` HR_BAND_MIN/MAX. */
export const HEIGHT_BAND_MIN = 0.6;
export const HEIGHT_BAND_MAX = 0.95;
/** `alignmentTuning.ts` HEIGHT_RATIO_MAX_STALENESS_MS. */
export const HEIGHT_MAX_STALENESS_MS = 1500;

export interface BlindSample {
  now: number;
  /** The latest height-model reading, as `PoseDetectionManager.heightRatio`. */
  heightRatio: number;
  /** When that reading was taken; null when there has been none this attempt. */
  heightRatioAt: number | null;
  /** Scores of the four gated joints, or null when no pose came back. */
  gatedScores: number[] | null;
}

function allBelow(scores: number[], floor: number): boolean {
  for (let i = 0; i < scores.length; i++) {
    if (!(scores[i] < floor)) return false;
  }
  return true;
}

export class BlindWhileVisible {
  private runStart: number | null = null;
  private lastAt: number | null = null;
  private longestMs = 0;
  private blind = false;

  observe(s: BlindSample): void {
    const visible =
      s.heightRatio > HEIGHT_BAND_MIN &&
      s.heightRatio <= HEIGHT_BAND_MAX &&
      s.heightRatioAt !== null &&
      s.now - s.heightRatioAt <= HEIGHT_MAX_STALENESS_MS;
    const collapsed =
      s.gatedScores === null || allBelow(s.gatedScores, BLIND_SCORE_FLOOR);

    if (!visible || !collapsed) {
      this.runStart = null;
      this.lastAt = null;
      return;
    }
    if (
      this.runStart === null ||
      this.lastAt === null ||
      s.now - this.lastAt > BLIND_GAP_TOLERANCE_MS
    ) {
      this.runStart = s.now;
    }
    this.lastAt = s.now;
    const run = s.now - this.runStart;
    if (run > this.longestMs) this.longestMs = run;
    if (run >= BLIND_AFTER_MS) this.blind = true;
  }

  isBlind(): boolean {
    return this.blind;
  }

  /** Longest blind-while-visible run this attempt, for telemetry. */
  blindMs(): number {
    return Math.round(this.longestMs);
  }

  reset(): void {
    this.runStart = null;
    this.lastAt = null;
    this.longestMs = 0;
    this.blind = false;
  }
}

/**
 * `EVENTS.md`'s line for "the device could not keep up" (`effectivePoseHz < 8`),
 * and the router's `live-degraded` trigger.
 */
export const PHONE_TOO_SLOW_HZ = 8;
/** The trailing window the phone's rate is read over. */
export const PHONE_RATE_WINDOW_MS = 4000;
/**
 * How much of that window the phone must have run — the gaps between its
 * frames, stalls left out — before its rate is a verdict.
 *
 * The rate used to count from three frames, so one late frame early in a run
 * was a verdict, and the latch below made it the answer for the attempt: three
 * frames from a 13 Hz phone, the third 450 ms late, read as 3.8 Hz.
 * Two seconds holds ~26 frames at 13 Hz, so a lone 500 ms stutter still reads
 * above 10 Hz, while a 5 Hz phone is moved after 2 s instead of 0.4 s. Keep it
 * under the 3.9 s the accumulator's 32 frames span at 8 Hz, or a phone near
 * the line could never be judged.
 */
export const PHONE_RATE_MIN_SPAN_MS = 2000;

/**
 * The phone is too slow on this attempt: its OWN frames arrive below
 * `PHONE_TOO_SLOW_HZ`.
 *
 * Fed by `alignmentAccumulator` with the rate of every frame the phone's
 * detector completed (`frameDetected`), whether or not it found anyone, and
 * never frames the cloud detected. Two earlier versions read the wrong frames:
 *   - every frame, whichever source. The cloud runs at 4-10 fps by nature, so
 *     on the 2026-09-14 dev build every cloud attempt read as "phone too slow"
 *     and re-latched session memory;
 *   - 2.5.21-2.5.25: only the phone's frames with a person box AND a pose. A
 *     person flickering at the edge of the frame left ~400 ms holes in a 13 Hz
 *     stream, and 86 of 379 iPhone visits to positioning (2026-09-17 to 10-12)
 *     were moved to the cloud on phones that were not slow.
 *
 * Latches for the attempt like the other two signals. Without the latch the
 * attempt bounces: the phone is judged slow, the cloud takes over, the phone's
 * frames age out of the window, and Auto sends the attempt back to the phone.
 */
export class PhoneTooSlow {
  private slow = false;

  /** @param hz the phone's own frame rate; `null` when there is too little to judge. */
  observe(hz: number | null): void {
    if (hz !== null && hz < PHONE_TOO_SLOW_HZ) this.slow = true;
  }

  isSlow(): boolean {
    return this.slow;
  }

  reset(): void {
    this.slow = false;
  }
}

/**
 * "Struggling to get into position" — but only the kind another model can fix.
 *
 * ── Why not a timer ───────────────────────────────────────────────────────
 *
 * The obvious rule, "switch after N seconds without reaching the rotation",
 * fires on most healthy users. On 1,344 prod alignment summaries
 * (2026-08-24 to 09-16) a SUCCESSFUL attempt took a median 32.5 s; a 30 s timer
 * would have moved 52% of people who were about to succeed, and 60 s still 28%.
 *
 * And most slow attempts are not a model problem. Of the successes that took
 * over a minute, 59% had every gated joint detected and simply OUTSIDE its box
 * for 30%+ of the time — the person standing or holding their arms wrong. The
 * other model sees the same arm in the same place; sending a healthy phone to
 * the cloud only lowers its frame rate. 36% instead had one gated joint
 * UNDETECTED for 30%+ of the time, against 5% of attempts that succeeded
 * inside 20 s. That signature is what this measures.
 *
 * ── What counts ───────────────────────────────────────────────────────────
 *
 * The same definitions the evidence was measured with, fed by
 * `alignmentAccumulator` from the exact values it records: a joint is unseen
 * when `keyPointInFrame`'s `score > 0.3` fails or the model returned no
 * keypoint for it; the person is framed when the height ratio is in
 * (0.6, 0.95]; and no time is charged across a > 500 ms dropout. Only framed
 * time counts toward either side — a wrist out of shot because the person is
 * too close is placement too.
 *
 * On the replayed prod rows, "one joint unseen for >= 5 s and >= 30% of framed
 * time" held on 4% of successes under 20 s, 16% of those taking 20-60 s, 50% of
 * those over a minute, and 69% of attempts people gave up on.
 *
 * ── Which model, and only once ────────────────────────────────────────────
 *
 * It judges the model producing frames NOW: a change of source restarts the
 * count, so four seconds on the phone and four on the cloud are not eight of
 * either. And it latches, like the other signals here — the first source to trip
 * is the verdict for the attempt. Without the latch the switch itself would
 * restart the count, the new model's first frames would look healthy, and Auto
 * would send the attempt straight back.
 */
export const JOINT_UNSEEN_MIN_MS = 5000;
export const JOINT_UNSEEN_MIN_SHARE = 0.3;

/** Local on purpose: this module imports nothing (see the header). */
export type LiveSource = "device" | "server";

export interface JointSightSample {
  /** The model that produced this frame. */
  source: LiveSource;
  /** Milliseconds this frame may charge: the gap since the last one, 0 across a dropout. */
  chargeMs: number;
  /** The person is framed — height ratio in (0.6, 0.95]. */
  framed: boolean;
  /** One entry per gated joint: `true` when the model did not see it. */
  unseen: boolean[];
}

export class JointUnseenWhileFramed {
  private source: LiveSource | null = null;
  private framedMs = 0;
  private unseenMs: number[] = [];
  private worstMs = 0;
  private tripped: LiveSource | null = null;

  observe(s: JointSightSample): void {
    if (this.tripped !== null) return;
    if (s.source !== this.source) {
      this.source = s.source;
      this.framedMs = 0;
      this.unseenMs = [];
      this.worstMs = 0;
    }
    if (!s.framed || !(s.chargeMs > 0)) return;

    this.framedMs += s.chargeMs;
    let worst = 0;
    for (let i = 0; i < s.unseen.length; i++) {
      const ms = (this.unseenMs[i] || 0) + (s.unseen[i] ? s.chargeMs : 0);
      this.unseenMs[i] = ms;
      if (ms > worst) worst = ms;
    }
    this.worstMs = worst;

    if (
      worst >= JOINT_UNSEEN_MIN_MS &&
      worst >= JOINT_UNSEEN_MIN_SHARE * this.framedMs
    ) {
      this.tripped = s.source;
    }
  }

  /** The model that could not see a joint this attempt, or `null`. */
  unseenSource(): LiveSource | null {
    return this.tripped;
  }

  /** The most framed time any one joint went unseen, for telemetry. */
  worstUnseenMs(): number {
    return Math.round(this.worstMs);
  }

  reset(): void {
    this.source = null;
    this.framedMs = 0;
    this.unseenMs = [];
    this.worstMs = 0;
    this.tripped = null;
  }
}

export const detectorHealth = new DetectorHealth();
export const blindWhileVisible = new BlindWhileVisible();
export const phoneTooSlow = new PhoneTooSlow();
export const jointUnseenWhileFramed = new JointUnseenWhileFramed();
